import { EyeIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { ClickableCount } from '@/components/ClickableCount';
import { Divider } from '@/components/Divider';
import { Container } from '@/components/legacy-ui';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { monitoredAssetsCategoryNameMap } from '@/types';
import { cn } from '@/utils/classname';

import { AssetMonitoringSectionProps } from './types';

/**
 * AssetMonitoringSection - Complete asset monitoring section component
 *
 * Displays asset metrics in a bordered section with:
 * - Section header with title and optional "View" button
 * - 3-column grid of asset category cards
 * - Hierarchical data display (total + breakdown by category)
 * - Clickable totals and categories (optional)
 * - Loading states with skeleton UI
 * - Tooltips for category explanations (TODO)
 *
 * @example
 * // Basic usage with navigation
 * <AssetMonitoringSection
 *   sections={assetSections}
 *   onViewAll={() => navigate('/assets')}
 *   onSectionClick={(key) => navigate(`/assets?section=${key}`)}
 *   onCategoryClick={(categoryKey, sectionKey) => navigate(`/assets?category=${categoryKey}`)}
 * />
 *
 * @example
 * // Read-only mode (no actions)
 * <AssetMonitoringSection
 *   sections={assetSections}
 *   showAction={false}
 * />
 */
export const AssetMonitoringSection: React.FC<AssetMonitoringSectionProps> = ({
  sections,
  isLoading = false,
  onViewAll,
  onSectionClick,
  onCategoryClick,
  title = 'Asset Monitoring',
  actionLabel = 'View Assets',
  showAction = true,
  className,
  headerExtra,
}) => {
  return (
    <Container variant="emphasized" className={cn(className)}>
      {/* Section Header */}
      <div className="mb-4 flex items-center gap-3">
        <h3 className="text-xl font-bold">{title}</h3>
        {headerExtra}
        {showAction && onViewAll && (
          <Button
            className="ml-auto rounded-full"
            size="sm"
            variant="outline"
            onClick={onViewAll}
          >
            <EyeIcon className="size-5" />
            {actionLabel}
          </Button>
        )}
      </div>

      {/* Asset Sections Grid */}
      <div className="grid grid-cols-3 gap-4">
        {sections.map(section => (
          <AssetSectionCard
            key={section.key}
            section={section}
            isLoading={isLoading}
            onTotalClick={
              onSectionClick ? () => onSectionClick(section.key) : undefined
            }
            onCategoryClick={
              onCategoryClick
                ? (categoryKey: string) =>
                    onCategoryClick(categoryKey, section.title)
                : undefined
            }
          />
        ))}
      </div>
    </Container>
  );
};

/**
 * Internal component for individual asset section card
 */
const AssetSectionCard: React.FC<{
  section: AssetMonitoringSectionProps['sections'][0];
  isLoading: boolean;
  onTotalClick?: () => void;
  onCategoryClick?: (categoryKey: string) => void;
}> = ({ section, isLoading, onTotalClick, onCategoryClick }) => {
  return (
    <Container variant="emphasized" className="relative min-w-32 flex-1 p-4">
      {/* Section Title Badge */}
      <h6 className="bg-chariot-brand-primary absolute -top-3 left-4 rounded-xs px-2 py-1 text-xs font-bold text-white uppercase">
        {section.title}
      </h6>

      {/* Section Content */}
      {isLoading ? (
        <Skeleton className="min-h-24 min-w-14" />
      ) : (
        <div className="flex flex-col gap-2">
          {/* Total Count */}
          {onTotalClick ? (
            <ClickableCount
              count={section.total}
              onClick={onTotalClick}
              size="md"
              className="font-bold"
            />
          ) : (
            <span className="text-2xl font-bold">{section.total}</span>
          )}

          <Divider className="h-[1px]" />

          {/* Category Breakdown */}
          <div className="text-chariot-text-secondary flex flex-col gap-1 text-xs">
            {section.categories.map(category => (
              <div key={category.key}>
                <span className="font-bold">{`${monitoredAssetsCategoryNameMap[category.label] || category.label}: `}</span>
                {onCategoryClick ? (
                  <ClickableCount
                    count={category.count}
                    onClick={() => onCategoryClick(category.label)}
                  />
                ) : (
                  <span>{category.count}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Container>
  );
};
